import axios, { AxiosError } from "axios";
import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { generatePath } from "react-router-dom";
import {
  ActionList,
  DropdownInput,
  IOption,
  PageHeader,
  Search,
  getErrorPopupTexts,
} from "component-library";
import { MapDispatchToProps } from "shared/types/utilities";
import {
  BREAKDOWN_FULL_PATH,
  JOB_UPDATE_NAVIGATION_DELAY,
} from "config/common";
import PageKey from "config/PageKey";
import { IApplicationState } from "store";
import { hidePopup, showPopup } from "store/ui/ui.actions";
import { HidePopup, PopupType, ShowPopup } from "store/ui/ui.types";
import { IWithRequestAbortController } from "types/axios";
import {
  ClientAlertID,
  IClientAlert,
  IPaginatedClientAlertList,
} from "models/clientAlert";
import { IListItem } from "models/common";
import getErrorPopupType from "helpers/getErrorPopupType";
import suggestionsService from "services/api/suggestions/suggestionsService";
import {
  GetClientAlerts,
  IClientAlertsRequestParams,
  SetClientAlerts,
} from "services/api/suggestions/suggestionsService.types";

import {
  ClientAlertsFilterControl,
  IFilterValues,
} from "components/ClientAlertsFilterControl/ClientAlertsFilterControl";
import ClientAlertsView from "components/ClientAlertsView/ClientAlertsView";

import { IJobsPageInjectedProps } from "../JobsPage";

import {
  clientAlertConverter,
  ClientAlertSortingType,
  getFilteringOptionsFomFilterValues,
  prepareClientAlertsTexts,
  sortingOptionsToSortFilterMapping,
} from "./ClientAlertsPage.helpers";

import "./ClientAlertsPage.scss";
import { RouterProps } from "hoc/withRouter";

const SEARCH_MAX_LENGTH = 55;

export interface IClientAlertsPageTexts {
  dateTimeFormat: string;
  clientAlertTitleNudge: string;
  clientAlertTitleSuggestion: string;
  suggested: string;
  nudged: string;
  closingDateLabels: IListItem[];
  resultStats: string;
  selectAllAlertsLabel: string;
  markAsReadAlertsLabel: string;
  markAsUnreadAlertsLabel: string;
  searchPlaceholder: string;
  filterDateFilter: string;
  sentOnFormat: string;
}

export interface IClientAlertsPageStoreProps {
  locale: string;
  texts: IClientAlertsPageTexts;
  getClientAlerts: GetClientAlerts;
  markAsRead: SetClientAlerts;
  markAsUnread: SetClientAlerts;
}

export interface IClientAlertsPageActions {
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export interface IClientAlertsPageProps
  extends IClientAlertsPageStoreProps,
    IClientAlertsPageActions,
    IJobsPageInjectedProps,
    RouterProps {}

export interface IClientAlertsPageState {
  selectedItems: ClientAlertID[];
  pageNumber: number;
  isLoading: boolean;
  paginatedClientAlerts: IPaginatedClientAlertList;
  sortingOption: ClientAlertSortingType;
  filterValues?: IFilterValues;
  search?: string;
}

export class ClientAlertsPage
  extends React.PureComponent<IClientAlertsPageProps, IClientAlertsPageState>
  implements IWithRequestAbortController
{
  public abortController = new AbortController();
  public static pageSize: number = 50;
  public static defaultPageNumber = 1;

  public readonly state: Readonly<IClientAlertsPageState> = {
    selectedItems: [],
    pageNumber: ClientAlertsPage.defaultPageNumber,
    isLoading: false,
    paginatedClientAlerts: {
      count: 0,
      items: [],
    },
    sortingOption: ClientAlertSortingType.DateSentDesc,
  };

  public componentDidMount() {
    this.loadClientAlerts();
  }

  public componentWillUnmount() {
    this.abortController.abort?.();
  }

  public render() {
    const { texts, locale, commonTexts } = this.props;
    const { pageNumber, selectedItems, paginatedClientAlerts, isLoading } =
      this.state;

    return (
      <>
        <PageHeader
          pageTitle={commonTexts.title}
          bottomSection={this.renderBottomSection()}
          actionSection={
            <ActionList variant="pageHeader" items={[]} moreButtonText={""} />
          }
        />
        <ClientAlertsFilterControl
          locale={locale}
          texts={texts}
          onValueChange={this.handleFilterValuesChange}
        />
        <div className="g-content-area">
          <div className="g-col-lg-12">
            <ClientAlertsView
              texts={texts}
              items={paginatedClientAlerts.items}
              selectedItems={selectedItems}
              deselectItems={this.deselectItems}
              selectItems={this.selectItems}
              onClientAlertClick={this.handleClientAlertClick}
              itemsPerPage={ClientAlertsPage.pageSize}
              isPageLoading={isLoading}
              pageNumber={pageNumber}
              itemsCount={paginatedClientAlerts.count}
              updatePageNumber={this.handlePageChange}
              onMarkAsRead={this.handleMarkItemsAsRead}
              onMarkAsUnread={this.handleMarkItemsAsUnread}
            />
          </div>
        </div>
      </>
    );
  }

  private renderBottomSection = () => {
    const { texts, commonTexts } = this.props;

    return (
      <div className="g-inner-grid-12 c-client-alerts-page__page-header-bottom-section">
        <div className="g-col-lg-6">
          <Search
            maxLength={SEARCH_MAX_LENGTH}
            onSearch={this.handleSearch}
            placeholder={texts.searchPlaceholder}
            onClear={this.handleSearchClear}
          />
        </div>
        <div className="g-col-lg-3 g-col-sm-0" />
        <div className="g-col-lg-3 g-col-sm-12">
          <DropdownInput<string>
            id="clientAlertsSorting"
            variant="select"
            options={commonTexts.sortingOptions}
            selected={this.getSortingOption(this.state.sortingOption)}
            optionSelected={this.handleSortingChange}
          />
        </div>
      </div>
    );
  };

  private getSortingOption = (
    value: ClientAlertSortingType = ClientAlertSortingType.DateSentDesc
  ) => {
    const { sortingOptions } = this.props.commonTexts;

    return (
      sortingOptions.find((option) => option.value === value) ||
      sortingOptions[0]
    );
  };

  private handleSortingChange = (option: IOption<ClientAlertSortingType>) => {
    this.setState(
      {
        sortingOption: option.value,
        pageNumber: ClientAlertsPage.defaultPageNumber,
      },
      this.loadClientAlerts
    );
  };

  private handlePageChange = (pageNumber: number) => {
    this.setState({ pageNumber, selectedItems: [] }, this.loadClientAlerts);
  };

  private handleSearchClear = () => {
    this.handleSearch();
  };

  private handleSearch = (search: string = "") => {
    this.setState({ search }, this.loadClientAlerts);
  };

  private handleFilterValuesChange = (values: IFilterValues) => {
    if (JSON.stringify(this.state.filterValues) !== JSON.stringify(values)) {
      this.setState({ filterValues: values }, this.loadClientAlerts);
    }
  };

  private handleMarkItemsAsRead = async (clientAlertId: string[]) => {
    await this.toggleReadStatus(clientAlertId, this.props.markAsRead);
  };

  private handleMarkItemsAsUnread = async (clientAlertId: string[]) => {
    await this.toggleReadStatus(clientAlertId, this.props.markAsUnread);
  };

  private handleClientAlertClick = async (item: IClientAlert) => {
    const { router } = this.props;
    await this.props.markAsRead([item.id]);
    router.navigate(
      generatePath(BREAKDOWN_FULL_PATH, { breakdownId: item.breakdown.id })
    );
  };

  private handleError(e: AxiosError) {
    if (axios.isCancel(e)) {
      return;
    }

    this.props.showPopup({
      type: PopupType.ErrorPopup,
      traceID: e?.response?.headers?.["x-trace-identifier"],
      data: {
        ...getErrorPopupTexts()[getErrorPopupType()],
        hidePopup: this.props.hidePopup,
      },
    });
  }

  private selectItems = (clientAlertId: string[]) => {
    this.setState({
      selectedItems: [...this.state.selectedItems, ...clientAlertId],
    });
  };

  private deselectItems = (clientAlertId: string[]) => {
    this.setState({
      selectedItems: this.state.selectedItems.filter(
        (item) => !clientAlertId.includes(item)
      ),
    });
  };

  private async loadClientAlerts() {
    const { pageNumber, sortingOption, filterValues, search } = this.state;

    this.setState({ isLoading: true });
    this.abortController.abort?.();
    this.abortController = new AbortController();
    try {
      const params: IClientAlertsRequestParams = {
        skip: (pageNumber - 1) * ClientAlertsPage.pageSize,
        take: ClientAlertsPage.pageSize,
        sortOption: sortingOptionsToSortFilterMapping[sortingOption],
        filteringOptions: getFilteringOptionsFomFilterValues(filterValues),
        search,
      };
      const alerts = await this.props.getClientAlerts(
        params,
        this.abortController
      );

      this.setState({
        paginatedClientAlerts: {
          count: alerts.total ?? 0,
          items: alerts.items ? clientAlertConverter.convert(alerts.items) : [],
        },
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setState({ isLoading: false, selectedItems: [] });
    }
  }

  private async toggleReadStatus(
    clientAlertId: ClientAlertID[],
    handler: SetClientAlerts
  ) {
    this.setState({ isLoading: true });

    try {
      await handler(clientAlertId);
      setTimeout(() => {
        this.loadClientAlerts();
        this.props.refreshCounters();
      }, JOB_UPDATE_NAVIGATION_DELAY);
    } catch (e) {
      this.handleError(e);
      this.setState({ isLoading: false });
    }
  }
}

const mapStateToProps = (
  state: IApplicationState
): IClientAlertsPageStoreProps => {
  const pageTranslations = state.translation.pages[PageKey.Jobs]!;
  const commonTranslations = state.translation.common;

  return {
    locale: state.ui.locale,
    texts: prepareClientAlertsTexts(pageTranslations, commonTranslations),
    getClientAlerts: (...args) => suggestionsService.getClientAlerts(...args),
    markAsRead: (...args) => suggestionsService.markAsRead(...args),
    markAsUnread: (...args) => suggestionsService.markAsUnread(...args),
  };
};

const mapDispatchToProps: MapDispatchToProps<IClientAlertsPageActions> = {
  showPopup,
  hidePopup,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ClientAlertsPage
);
